import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  Coins,
  Edit2,
  Filter,
  Loader2,
  Plus,
  Power,
  Search,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import { formatCents } from '../lib/money'
import { useServiceLines } from '../lib/service-lines'
import { useTableSort } from '../lib/useTableSort'
import {
  RateKindLabel,
  RateKindShort,
  type CreateRateInput,
  type RateDto,
  type RateKindT,
  type RateListResponse,
} from '@jr2os/shared'

const KIND_OPTIONS = Object.keys(RateKindLabel) as RateKindT[]

const KIND_PILL: Record<RateKindT, string> = {
  HORA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  SERVICIO: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
  IGUALA_MENSUAL: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40',
  HITO: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  SUSCRIPCION_INFRA: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/40',
}

export default function Rates() {
  const { t } = useTranslation(['rates', 'common'])
  const { nameOf, allLines } = useServiceLines()
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const [items, setItems] = useState<RateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [kindFilter, setKindFilter] = useState<RateKindT | ''>('')
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('true')
  const [editing, setEditing] = useState<RateDto | 'new' | null>(null)

  // Orden por columna (en el navegador: /rates devuelve el catálogo entero). Sin orden elegido, el de siempre. El tipo ordena por
  // la etiqueta que ve el usuario, la línea por su nombre (de ahí `allLines`: se reordena cuando llega el catálogo de líneas) y
  // el estado con las activas primero, como el orden de siempre.
  const { rows, header } = useTableSort(
    items,
    {
      name: (r) => r.name,
      kind: (r) => t('enums:RateKind.' + r.kind),
      serviceLine: (r) => (r.serviceLine ? nameOf(r.serviceLine) : null),
      amount: (r) => r.amountCents,
      vat: (r) => r.vatRate,
      status: (r) => (r.isActive ? 0 : 1),
    },
    { deps: [allLines] }
  )

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (kindFilter) params.set('kind', kindFilter)
      if (activeFilter) params.set('active', activeFilter)
      const { items } = await api.get<RateListResponse>(`/rates?${params.toString()}`)
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.load'))
    } finally {
      setLoading(false)
    }
  }, [q, kindFilter, activeFilter, t])

  useEffect(() => {
    void reload()
  }, [reload])

  const onDelete = async (r: RateDto) => {
    if (!(await confirmDialog({ message: t('confirm.delete', { name: r.name }), tone: 'danger' })))
      return
    try {
      await api.delete(`/rates/${r.id}`)
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  const onToggleActive = async (r: RateDto) => {
    try {
      await api.patch(`/rates/${r.id}`, { isActive: !r.isActive })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Coins size={20} className="text-jr2-700 dark:text-jr2-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">
            {t('subtitle')}
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('newButton')}
          </button>
        )}
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-jr2-500/50"
          />
        </div>
        <label className="text-xs t-muted flex items-center gap-2">
          <Filter size={12} className="t-faint" /> {t('filters.kind')}
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as RateKindT | '')}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
          >
            <option value="">{t('filters.allKinds')}</option>
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {t('enums:RateKind.' + k)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs t-muted flex items-center gap-2">
          {t('filters.status')}
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as '' | 'true' | 'false')}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
          >
            <option value="">{t('filters.allStatuses')}</option>
            <option value="true">{t('filters.active')}</option>
            <option value="false">{t('filters.inactive')}</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              {header('name', t('table.name'))}
              {header('kind', t('table.kind'))}
              {header('serviceLine', t('table.serviceLine'))}
              {header('amount', t('table.amount'), { align: 'right' })}
              {header('vat', t('table.vat'), { align: 'right' })}
              {header('status', t('table.status'))}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center">
                  <Coins size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {q || kindFilter
                      ? t('empty.filtered')
                      : t('empty.none')}
                  </p>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-base/40 hover:bg-hover cursor-pointer"
                onClick={() => setEditing(r)}
              >
                <td className="px-3 py-2 t-primary font-medium">{r.name}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${KIND_PILL[r.kind]}`}
                  >
                    {t('enums:RateKind.' + r.kind)}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs t-secondary">
                  {r.serviceLine ? (
                    nameOf(r.serviceLine)
                  ) : (
                    <span className="t-faint">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right t-primary font-mono">
                  {formatCents(r.amountCents)} {RateKindShort[r.kind]}
                </td>
                <td className="px-3 py-2 text-right t-secondary text-xs">
                  {(r.vatRate * 100).toFixed(0)}%
                </td>
                <td className="px-3 py-2">
                  {r.isActive ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] border bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40">
                      {t('status.active')}
                    </span>
                  ) : (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] border bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500/40">
                      {t('status.inactive')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  {canWrite && (
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void onToggleActive(r)}
                        title={r.isActive ? t('actions.deactivate') : t('actions.activate')}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                      >
                        <Power size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        title={t('actions.edit')}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(r)}
                        title={t('actions.delete')}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <RateFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void reload()
          }}
        />
      )}
    </div>
  )
}

interface FormProps {
  initial: RateDto | null
  onClose: () => void
  onSaved: () => void
}

function RateFormModal({ initial, onClose, onSaved }: FormProps) {
  const { t } = useTranslation(['rates', 'common'])
  const { lines } = useServiceLines()
  const isEdit = initial !== null
  const [form, setForm] = useState<{
    name: string
    kind: RateKindT
    amountEuros: string
    costEuros: string
    vatPct: string
    serviceLine: string
    description: string
    isActive: boolean
  }>({
    name: initial?.name ?? '',
    kind: initial?.kind ?? 'HORA',
    amountEuros: initial ? (initial.amountCents / 100).toFixed(2).replace('.', ',') : '',
    costEuros:
      initial?.costCents != null ? (initial.costCents / 100).toFixed(2).replace('.', ',') : '',
    vatPct: initial ? (initial.vatRate * 100).toFixed(0) : '21',
    serviceLine: initial?.serviceLine ?? '',
    description: initial?.description ?? '',
    isActive: initial?.isActive ?? true,
  })
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const upd = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const eurosNumber = Number(form.amountEuros.replace(',', '.'))
      if (Number.isNaN(eurosNumber) || eurosNumber < 0) {
        setSubmitting(false)
        return setError(t('form.errors.amount'))
      }
      const vatNumber = Number(form.vatPct.replace(',', '.'))
      if (Number.isNaN(vatNumber) || vatNumber < 0 || vatNumber > 100) {
        setSubmitting(false)
        return setError(t('form.errors.vat'))
      }

      const costTrimmed = form.costEuros.trim()
      let costCents: number | null = null
      if (costTrimmed) {
        const costNumber = Number(costTrimmed.replace(',', '.'))
        if (Number.isNaN(costNumber) || costNumber < 0) {
          setSubmitting(false)
          return setError(t('form.errors.cost'))
        }
        costCents = Math.round(costNumber * 100)
      }

      const payload: CreateRateInput = {
        name: form.name.trim(),
        kind: form.kind,
        amountCents: Math.round(eurosNumber * 100),
        costCents,
        vatRate: vatNumber / 100,
        serviceLine: form.serviceLine === '' ? null : form.serviceLine,
        description: form.description.trim() || null,
        isActive: form.isActive,
      }
      if (isEdit && initial) {
        await api.patch(`/rates/${initial.id}`, payload)
      } else {
        await api.post('/rates', payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('form.errors.save'))
      } else {
        setError(t('form.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEdit ? t('form.editTitle') : t('form.newTitle')}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.name')}</label>
          <input
            required
            value={form.name}
            onChange={(e) => upd('name', e.target.value)}
            placeholder={t('form.namePlaceholder')}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.kind')}</label>
            <select
              value={form.kind}
              onChange={(e) => upd('kind', e.target.value as RateKindT)}
              className={inputCls}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {t('enums:RateKind.' + k)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">
              {t('form.amount', { unit: RateKindShort[form.kind] })}
            </label>
            <input
              required
              inputMode="decimal"
              value={form.amountEuros}
              onChange={(e) => upd('amountEuros', e.target.value)}
              placeholder="120,00"
              className={`${inputCls} font-mono text-right`}
            />
          </div>
          <div>
            <label
              className="block text-xs text-amber-700 dark:text-amber-400/70 mb-1.5"
              title={t('form.costTooltip')}
            >
              {t('form.cost')} <span className="text-[9px]">{t('form.costInternal')}</span>
            </label>
            <input
              inputMode="decimal"
              value={form.costEuros}
              onChange={(e) => upd('costEuros', e.target.value)}
              placeholder="0,00"
              title={t('form.costTitle')}
              className="w-full px-3 py-2 rounded-lg bg-amber-500/[0.04] border border-amber-500/40 text-amber-800 dark:text-amber-200/90 font-mono text-right focus:outline-none focus:ring-1 focus:ring-amber-500/40 focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.vat')}</label>
            <input
              required
              inputMode="decimal"
              value={form.vatPct}
              onChange={(e) => upd('vatPct', e.target.value)}
              placeholder="21"
              className={`${inputCls} font-mono text-right`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.serviceLine')}</label>
            <select
              value={form.serviceLine}
              onChange={(e) => upd('serviceLine', e.target.value)}
              className={inputCls}
            >
              <option value="">{t('form.noServiceLine')}</option>
              {lines.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] t-faint mt-1">
              {t('form.serviceLineHint')}
            </p>
          </div>
          <div className="flex items-end pb-2">
            <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => upd('isActive', e.target.checked)}
                className="rounded accent-jr2-500"
              />
              {t('form.isActive')}
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.description')}</label>
          <textarea
            value={form.description}
            onChange={(e) => upd('description', e.target.value)}
            rows={3}
            placeholder={t('form.descriptionPlaceholder')}
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? t('form.saveChanges') : t('form.create')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
